/**
 * 1.4.0 asset groups, stream G2: Administration → Metadata model → Group types (?view=group-types, ModelEditor.tsx;
 * permission admin). The list with the nesting (Series › Season); the editor for label, plural, parent type, member
 * label, numbering and the number's label; the fields (the model editor's FieldInspector, over a one-section model of
 * the type's own fields); Inheritance: a table "Group field → Asset field", each row on or off, with the note "shown on
 * members whose own field is empty; never overwrites the asset's metadata"; the recompute's status line.
 * Hooks: api/hooks/groups.ts. G2's file.
 */
import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Archive, Layers, Loader2, Plus, RotateCcw, Save, Trash2, Tv, X } from 'lucide-react';
import {
  GROUP_NUMBERING, GROUP_TYPE_KEY, newFieldIdProblem,
  type FieldDefinition, type GroupInheritMapping, type GroupNumbering, type GroupTypeDTO, type MetadataSchemaDefinition,
} from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { useMe, useSchema } from '../../api/hooks';
import { useCreateGroupType, useDeleteGroupType, useGroupTypes, usePatchGroupType } from '../../api/hooks/groups';
import { Modal } from '../../components/Modal';
import { toast } from '../../components/Toasts';
import { relative } from '../../lib/format';
import { FieldInspector, TYPE_LABELS } from '../admin/model/FieldInspector';
import { suggestFieldId } from '../admin/model/modelDraftOps';

export const NUMBERING_LABEL: Record<GroupNumbering, string> = {
  none: 'Not numbered',
  number: 'Numbered 1…n in the group',
  number_in_parent: 'Numbered 1…n across the parent group',
};

interface Draft {
  label: string;
  pluralLabel: string;
  parentTypeId: string | null;
  memberLabel: string;
  numbering: GroupNumbering;
  numberLabel: string;
  fields: FieldDefinition[];
  inherit: GroupInheritMapping[];
}
const draftOf = (t: GroupTypeDTO): Draft => ({
  label: t.label, pluralLabel: t.pluralLabel, parentTypeId: t.parentTypeId, memberLabel: t.memberLabel, numbering: t.numbering,
  numberLabel: t.numberLabel ?? '', fields: structuredClone(t.fields), inherit: structuredClone(t.inherit),
});
const SECTION = 'group';

/** The types in nesting order: each top-level type, then its child types below it (depth by the parent chain). */
export function typesInTreeOrder(items: GroupTypeDTO[]): Array<{ type: GroupTypeDTO; depth: number; path: string[] }> {
  const out: Array<{ type: GroupTypeDTO; depth: number; path: string[] }> = [];
  const byParent = (id: string | null) => items.filter((t) => (t.parentTypeId ?? null) === id).sort((a, b) => a.position - b.position || a.label.localeCompare(b.label));
  const walk = (id: string | null, depth: number, path: string[]) => {
    for (const t of byParent(id)) {
      if (out.some((o) => o.type.id === t.id)) continue;
      out.push({ type: t, depth, path: [...path, t.label] });
      walk(t.id, depth + 1, [...path, t.label]);
    }
  };
  walk(null, 0, []);
  for (const t of items) if (!out.some((o) => o.type.id === t.id)) out.push({ type: t, depth: 0, path: [t.label] });
  return out;
}

function RecomputeLine({ r }: { r: NonNullable<ReturnType<typeof useGroupTypes>['data']>['recompute'] }) {
  if (!r) return <span className="faint" data-testid="group-recompute">Inherited values: nothing recomputed yet.</span>;
  const pct = r.total ? Math.round((r.done / r.total) * 100) : 100;
  return (
    <span className={r.state === 'failed' ? 'danger-text' : 'faint'} data-testid="group-recompute" data-state={r.state}>
      {r.state === 'running' || r.state === 'queued'
        ? <><Loader2 size={12} className="spin" /> Recomputing inherited values: {r.done.toLocaleString()} of {r.total.toLocaleString()} assets ({pct} %)</>
        : r.state === 'failed'
          ? <>Recomputing inherited values failed{r.error ? `: ${r.error}` : ''}{r.finishedAt ? ` (${relative(r.finishedAt)})` : ''}</>
          : <>Inherited values up to date: {r.total.toLocaleString()} asset{r.total === 1 ? '' : 's'} recomputed {r.finishedAt ? relative(r.finishedAt) : ''}</>}
    </span>
  );
}

export function GroupTypesAdmin() {
  const isAdmin = useMe().data?.permissions.includes('admin') ?? false;
  const q = useGroupTypes();
  const items = useMemo(() => q.data?.items ?? [], [q.data]);
  const tree = useMemo(() => typesInTreeOrder(items), [items]);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  useEffect(() => { if (!selected && tree[0]) setSelected(tree[0].type.id); }, [tree, selected]);
  const type = items.find((t) => t.id === selected) ?? null;

  return (
    <div className="group-types-admin" data-testid="group-types-admin">
      <p className="lede">
        Group types are the levels assets are grouped into: a <b>Series</b> holds <b>Seasons</b>, a Season holds its episodes. Each type has its own fields, and members
        can show chosen group values where their own field is empty.
      </p>
      <div className="group-types-layout">
        <section className="panel-card group-types-list" aria-label="Group types">
          <div className="group-types-list-head">
            <strong>Types</strong>
            <span style={{ flex: 1 }} />
            {isAdmin && <button className="btn ghost sm" onClick={() => setCreating(true)} data-testid="group-type-new"><Plus size={13} /> New type</button>}
          </div>
          {q.isPending ? <div className="skeleton" style={{ height: 120 }} /> : q.isError ? <p className="danger-text">{errorMessage(q.error)}</p> : (
            <ul role="listbox" aria-label="Group types">
              {tree.map(({ type: t, depth, path }) => (
                <li key={t.id} role="option" aria-selected={t.id === selected} className={`group-type-row ${t.id === selected ? 'selected' : ''} ${t.archived ? 'archived' : ''}`}
                  style={{ paddingLeft: 10 + depth * 18 }} onClick={() => setSelected(t.id)} data-testid="group-type-row" data-key={t.key}>
                  {t.icon === 'tv' ? <Tv size={14} /> : <Layers size={14} />}
                  <span className="group-type-path" title={path.join(' › ')}>
                    {path.slice(0, -1).map((p) => <span key={p} className="faint">{p} › </span>)}<strong>{t.label}</strong>
                  </span>
                  <span className="faint group-type-count">{t.groupCount.toLocaleString()} {t.groupCount === 1 ? 'group' : 'groups'}</span>
                  {t.archived && <span className="pill">Archived</span>}
                </li>
              ))}
              {!tree.length && <li className="faint" style={{ padding: 12 }}>No group types.</li>}
            </ul>
          )}
          <div className="group-types-recompute">{q.data && <RecomputeLine r={q.data.recompute} />}</div>
        </section>
        {type ? <TypeEditor key={`${type.id}:${type.updatedAt}`} type={type} all={items} canEdit={isAdmin} onDeleted={() => setSelected(null)} /> : <section className="panel-card faint">Choose a type.</section>}
      </div>
      {creating && <NewTypeDialog all={items} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); setSelected(id); }} />}
    </div>
  );
}

function NewTypeDialog({ all, onClose, onCreated }: { all: GroupTypeDTO[]; onClose: () => void; onCreated: (id: string) => void }) {
  const create = useCreateGroupType();
  const [label, setLabel] = useState('');
  const [plural, setPlural] = useState('');
  const [key, setKey] = useState<string | null>(null);
  const [parent, setParent] = useState('');
  const k = key ?? label.trim().toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^([^a-z])/, 't_$1').slice(0, 40);
  const keyBad = !GROUP_TYPE_KEY.test(k) ? 'A lowercase letter first, then letters, digits or _' : all.some((t) => t.key === k) ? 'Another type has this key' : null;
  async function save() {
    try {
      const t = await create.mutateAsync({ key: k, label: label.trim(), pluralLabel: (plural || `${label}s`).trim(), parentTypeId: parent || null });
      toast(`${t.label} added`);
      onCreated(t.id);
    } catch (err) { toast(errorMessage(err), 'error'); }
  }
  return (
    <Modal title="New group type" onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!label.trim() || Boolean(keyBad) || create.isPending} onClick={() => void save()} data-testid="group-type-create"><Plus size={14} /> Add type</button>
    </>}>
      <div className="field"><label htmlFor="gtn-label">Name</label><input id="gtn-label" className="input" value={label} maxLength={80} placeholder="e.g. Volume" onChange={(e) => setLabel(e.target.value)} data-testid="group-type-new-label" /></div>
      <div className="field"><label htmlFor="gtn-plural">Plural</label><input id="gtn-plural" className="input" value={plural} maxLength={80} placeholder={label ? `${label}s` : 'e.g. Volumes'} onChange={(e) => setPlural(e.target.value)} /></div>
      <div className="field"><label htmlFor="gtn-key">Key</label><input id="gtn-key" className="input mono" value={k} maxLength={40} onChange={(e) => setKey(e.target.value)} aria-invalid={Boolean(label && keyBad) || undefined} />
        <span className="help">{label && keyBad ? keyBad : 'Permanent: the API and saved searches refer to it.'}</span></div>
      <div className="field"><label htmlFor="gtn-parent">Inside</label>
        <select id="gtn-parent" className="select" value={parent} onChange={(e) => setParent(e.target.value)}>
          <option value="">Nothing: a top-level type</option>
          {all.filter((t) => !t.archived).map((t) => <option key={t.id} value={t.id}>a {t.label}</option>)}
        </select></div>
    </Modal>
  );
}

/** A type's fields as a one-section model, so the model editor's FieldInspector can edit them. */
const defOf = (fields: FieldDefinition[]): MetadataSchemaDefinition => ({ entityType: 'asset', sections: [{ id: SECTION, label: 'Group fields' }], fields });

function TypeEditor({ type, all, canEdit, onDeleted }: { type: GroupTypeDTO; all: GroupTypeDTO[]; canEdit: boolean; onDeleted: () => void }) {
  const patch = usePatchGroupType();
  const del = useDeleteGroupType();
  const assetFields = (useSchema().data?.definition.fields ?? []).filter((f) => !f.hidden);
  const [d, setD] = useState<Draft>(() => draftOf(type));
  const [field, setField] = useState<string | null>(null);
  const [newField, setNewField] = useState('');
  const dirty = JSON.stringify(d) !== JSON.stringify(draftOf(type));
  const set = (p: Partial<Draft>) => setD((x) => ({ ...x, ...p }));
  // A type's possible parents: not itself and not one of its descendants (no loops).
  const descendants = useMemo(() => {
    const out = new Set<string>([type.id]);
    let grew = true;
    while (grew) { grew = false; for (const t of all) if (t.parentTypeId && out.has(t.parentTypeId) && !out.has(t.id)) { out.add(t.id); grew = true; } }
    return out;
  }, [all, type.id]);
  const inUse = type.groupCount > 0;
  const editing = d.fields.find((f) => f.id === field) ?? null;
  const fieldProblem = newField.trim() ? newFieldIdProblem(suggestFieldId(newField), d.fields.map((f) => f.id)) : null;

  function addField() {
    const id = suggestFieldId(newField);
    if (!id || fieldProblem) return;
    set({ fields: [...d.fields, { id, label: newField.trim(), type: 'text', required: false, section: SECTION }] });
    setNewField('');
    setField(id);
  }
  function removeField(id: string) {
    set({ fields: d.fields.filter((f) => f.id !== id), inherit: d.inherit.filter((m) => m.groupField !== id) });
    setField(null);
  }
  async function save() {
    try {
      await patch.mutateAsync({ id: type.id, body: {
        label: d.label.trim(), pluralLabel: d.pluralLabel.trim(), parentTypeId: d.parentTypeId, memberLabel: d.memberLabel.trim() || 'Asset', numbering: d.numbering,
        numberLabel: d.numberLabel.trim() || null, fields: d.fields.map((f) => ({ ...f, section: SECTION })), inherit: d.inherit,
      } });
      const changedInherit = JSON.stringify(d.inherit) !== JSON.stringify(type.inherit) || JSON.stringify(d.fields) !== JSON.stringify(type.fields);
      toast(changedInherit ? `${d.label} saved; inherited values are being recomputed` : `${d.label} saved`);
    } catch (err) { toast(errorMessage(err), 'error'); }
  }
  async function archive(on: boolean) {
    try { await patch.mutateAsync({ id: type.id, body: { archived: on } }); toast(on ? `${type.label} archived` : `${type.label} restored`); } catch (err) { toast(errorMessage(err), 'error'); }
  }
  async function remove() {
    if (!window.confirm(`Delete the group type ${type.label}?`)) return;
    try { await del.mutateAsync(type.id); toast(`${type.label} deleted`); onDeleted(); } catch (err) { toast(errorMessage(err), 'error'); }
  }
  const usedAssetFields = new Set(d.inherit.map((m) => m.assetField));

  return (
    <section className="panel-card group-type-editor" data-testid="group-type-editor" data-key={type.key}>
      <div className="group-type-editor-head">
        <h2>{type.label}</h2>
        <span className="mono faint" title="The type's key, permanent">{type.key}</span>
        <span style={{ flex: 1 }} />
        {canEdit && (
          <>
            <button className="btn ghost sm" disabled={!dirty} onClick={() => setD(draftOf(type))}><RotateCcw size={13} /> Discard</button>
            <button className="btn primary sm" disabled={!dirty || !d.label.trim() || !d.pluralLabel.trim() || patch.isPending} onClick={() => void save()} data-testid="group-type-save">
              {patch.isPending ? <Loader2 size={13} className="spin" /> : <Save size={13} />} Save
            </button>
          </>
        )}
      </div>
      <fieldset disabled={!canEdit} className="group-type-form">
        <div className="field-row">
          <div className="field"><label htmlFor="gt-label">Name</label><input id="gt-label" className="input" value={d.label} maxLength={80} onChange={(e) => set({ label: e.target.value })} data-testid="group-type-label" /></div>
          <div className="field"><label htmlFor="gt-plural">Plural</label><input id="gt-plural" className="input" value={d.pluralLabel} maxLength={80} onChange={(e) => set({ pluralLabel: e.target.value })} data-testid="group-type-plural" /></div>
        </div>
        <div className="field-row">
          <div className="field"><label htmlFor="gt-parent">Inside</label>
            <select id="gt-parent" className="select" value={d.parentTypeId ?? ''} disabled={inUse} onChange={(e) => set({ parentTypeId: e.target.value || null })} data-testid="group-type-parent"
              title={inUse ? 'Groups of this type exist: its parent type cannot change' : undefined}>
              <option value="">Nothing: a top-level type</option>
              {all.filter((t) => !descendants.has(t.id) && (!t.archived || t.id === d.parentTypeId)).map((t) => <option key={t.id} value={t.id}>a {t.label}</option>)}
            </select>
            {inUse && <span className="help">{type.groupCount} {type.groupCount === 1 ? 'group exists' : 'groups exist'}, so the parent type is fixed.</span>}
          </div>
          <div className="field"><label htmlFor="gt-member">A member is called</label><input id="gt-member" className="input" value={d.memberLabel} maxLength={80} placeholder="Asset" onChange={(e) => set({ memberLabel: e.target.value })} data-testid="group-type-member" /></div>
        </div>
        <div className="field-row">
          <div className="field"><label htmlFor="gt-numbering">Members are</label>
            <select id="gt-numbering" className="select" value={d.numbering} onChange={(e) => set({ numbering: e.target.value as GroupNumbering })} data-testid="group-type-numbering">
              {GROUP_NUMBERING.map((n) => <option key={n} value={n}>{NUMBERING_LABEL[n]}</option>)}
            </select></div>
          <div className="field"><label htmlFor="gt-number">The group's own number</label>
            <input id="gt-number" className="input" value={d.numberLabel} maxLength={80} placeholder="Not shown (e.g. Season number)" onChange={(e) => set({ numberLabel: e.target.value })} data-testid="group-type-number-label" />
            <span className="help">Orders groups among their siblings, e.g. the season number. Empty: groups of this type have none.</span></div>
        </div>
      </fieldset>

      <h3 className="group-type-sub">Fields</h3>
      <div className="group-type-fields">
        <ul className="group-type-field-list" data-testid="group-type-fields">
          {d.fields.map((f) => (
            <li key={f.id}>
              <button type="button" className={`model-field ${f.id === field ? 'selected' : ''}`} onClick={() => setField(f.id === field ? null : f.id)} data-testid="group-type-field" data-field={f.id}>
                <span className="model-field-main"><span className="model-field-label">{f.label || 'Unnamed field'}</span><span className="model-field-meta">{TYPE_LABELS[f.type]} · <span className="mono">{f.id}</span></span></span>
              </button>
            </li>
          ))}
          {!d.fields.length && <li className="faint">No fields yet.</li>}
        </ul>
        {canEdit && (
          <div className="group-type-add-field">
            <input className="input" value={newField} maxLength={80} placeholder="New field, e.g. Producer" aria-label="New field's label" onChange={(e) => setNewField(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addField(); }} data-testid="group-type-new-field" aria-invalid={Boolean(fieldProblem) || undefined} />
            <button className="btn ghost sm" disabled={!newField.trim() || Boolean(fieldProblem)} onClick={addField} data-testid="group-type-add-field"><Plus size={13} /> Field</button>
            {fieldProblem && <span className="help">{fieldProblem}</span>}
          </div>
        )}
        {editing && (
          <div className="group-type-field-inspector" data-testid="group-type-field-inspector">
            <div className="model-drawer-head">
              <strong>{editing.label || 'Unnamed field'}</strong><span style={{ flex: 1 }} />
              <button className="btn ghost icon sm" aria-label="Close the field" onClick={() => setField(null)}><X size={14} /></button>
            </div>
            <fieldset disabled={!canEdit}>
              <FieldInspector field={editing} def={defOf(d.fields)} isNew={!type.fields.some((f) => f.id === editing.id)} published={type.fields.find((f) => f.id === editing.id) ?? null}
                onChange={(p) => set({ fields: d.fields.map((f) => (f.id === editing.id ? { ...f, ...p } : f)) })}
                onMove={(dir) => {
                  const i = d.fields.findIndex((f) => f.id === editing.id);
                  const j = i + dir;
                  if (j < 0 || j >= d.fields.length) return;
                  const next = [...d.fields];
                  [next[i], next[j]] = [next[j]!, next[i]!];
                  set({ fields: next });
                }}
                onRemove={() => removeField(editing.id)} />
            </fieldset>
          </div>
        )}
      </div>

      <h3 className="group-type-sub">Inheritance</h3>
      <p className="help group-inherit-note" data-testid="group-inherit-note">
        Members show these group values in grey: shown on members whose own field is empty; never overwrites the asset's metadata.
      </p>
      <table className="table compact group-inherit" data-testid="group-inherit">
        <thead><tr><th>Group field</th><th aria-hidden /><th>Asset field</th><th>On</th><th aria-hidden /></tr></thead>
        <tbody>
          {d.inherit.map((m, i) => {
            const gf = d.fields.find((f) => f.id === m.groupField);
            const af = assetFields.find((f) => f.id === m.assetField);
            const mismatch = gf && af && gf.type !== af.type && !(gf.type === 'text' && af.type === 'rich_text');
            const setRow = (p: Partial<GroupInheritMapping>) => set({ inherit: d.inherit.map((x, j) => (j === i ? { ...x, ...p } : x)) });
            return (
              <tr key={i} data-testid="group-inherit-row" className={m.enabled ? '' : 'rule-paused'}>
                <td>
                  <select className="select" aria-label="Group field" value={m.groupField} disabled={!canEdit} onChange={(e) => setRow({ groupField: e.target.value })} data-testid="group-inherit-from">
                    {d.fields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                </td>
                <td aria-hidden><ArrowRight size={13} className="faint" /></td>
                <td>
                  <select className="select" aria-label="Asset field" value={m.assetField} disabled={!canEdit} onChange={(e) => setRow({ assetField: e.target.value })} data-testid="group-inherit-to">
                    {!af && <option value={m.assetField}>{m.assetField} (not in the model)</option>}
                    {assetFields.filter((f) => f.id === m.assetField || !usedAssetFields.has(f.id)).map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                  {mismatch && <span className="help">{TYPE_LABELS[gf.type]} into {TYPE_LABELS[af.type]}: values that do not fit are not shown.</span>}
                </td>
                <td>
                  <button type="button" role="switch" aria-checked={m.enabled} className="switch" disabled={!canEdit} aria-label={`Inherit ${gf?.label ?? m.groupField}`}
                    onClick={() => setRow({ enabled: !m.enabled })} data-testid="group-inherit-switch"><span className="track" /> {m.enabled ? 'On' : 'Off'}</button>
                </td>
                <td>{canEdit && <button className="btn ghost icon sm" aria-label="Remove this row" onClick={() => set({ inherit: d.inherit.filter((_, j) => j !== i) })}><Trash2 size={13} /></button>}</td>
              </tr>
            );
          })}
          {!d.inherit.length && <tr><td colSpan={5} className="faint">Nothing is inherited.</td></tr>}
        </tbody>
      </table>
      {canEdit && (
        <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} disabled={!d.fields.length || !assetFields.length} data-testid="group-inherit-add"
          title={d.fields.length ? undefined : 'Add a field to the type first'}
          onClick={() => {
            const gf = d.fields.find((f) => !d.inherit.some((m) => m.groupField === f.id)) ?? d.fields[0]!;
            const af = assetFields.find((f) => !usedAssetFields.has(f.id) && f.type === gf.type) ?? assetFields.find((f) => !usedAssetFields.has(f.id)) ?? assetFields[0]!;
            set({ inherit: [...d.inherit, { groupField: gf.id, assetField: af.id, enabled: true }] });
          }}><Plus size={13} /> Inherit a field</button>
      )}

      {canEdit && (
        <div className="group-type-danger">
          <button className="btn ghost sm" onClick={() => void archive(!type.archived)} disabled={!type.archived && inUse}
            title={!type.archived && inUse ? 'Groups of this type exist' : undefined}><Archive size={13} /> {type.archived ? 'Restore' : 'Archive'}</button>
          <button className="btn ghost sm danger" onClick={() => void remove()} disabled={inUse || del.isPending} title={inUse ? 'Groups of this type exist: delete them first' : undefined} data-testid="group-type-delete">
            <Trash2 size={13} /> Delete type
          </button>
        </div>
      )}
    </section>
  );
}
